import type { RefObject } from 'react'
import { ProductList } from '@haus-storefront-react/product-list'
import type { SearchInputProps, SearchResult } from '@haus-storefront-react/shared-types'
import { SortOrder } from '@haus-storefront-react/shared-types'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  LazyLoad,
  Typography,
  useWidgetReadyAfterInitialLoad,
  WidgetReadyBoundary,
} from '@components'
import { WordPressPathPaginationStrategy, getPageFromLocation } from './wordpressPaginationStrategy'
import { Pagination } from './Pagination'
import ProductListItem from './ProductListItem'
import type { ProductListAttributes } from './_propsFn'
import './ProductListSkeleton.css'
import styles from './ProductList.module.scss'

const TERM_PARAM_NAME = 'term'
const FOCUS_FIRST_ITEM_DELAY_MS = 350
const INITIAL_SCROLL_DELAY_MS = 150

function getSearchTermFromUrl(): string {
  const params = new URLSearchParams(window.location.search)
  return params.get(TERM_PARAM_NAME)?.trim() ?? ''
}

/**
 * Focuses the first product list item after a page transition to a page > 1.
 * Only runs when the page value has changed so non-navigation updates do not steal focus.
 */
function FirstItemFocusEffect({
  products,
  firstItemRef,
}: {
  products: SearchResult[]
  firstItemRef: RefObject<HTMLDivElement | null>
}) {
  const prevPageRef = useRef<number | null>(null)

  useEffect(() => {
    if (products.length === 0) return
    if (typeof window === 'undefined') return
    const currentPage = getPageFromLocation(window.location)
    const pageChanged = currentPage !== prevPageRef.current
    prevPageRef.current = currentPage
    if (currentPage <= 1 || !pageChanged) return
    const id = window.setTimeout(() => {
      firstItemRef.current?.focus()
    }, FOCUS_FIRST_ITEM_DELAY_MS)
    return () => window.clearTimeout(id)
  }, [products, firstItemRef])
  return null
}

interface ProductListContentProps extends Pick<
  ProductListAttributes,
  'configStyles' | 'enablePagination' | 'showBadges' | 'showCta' | 'showInStock' | 'showPrice'
> {
  error: unknown
  firstItemRef?: RefObject<HTMLDivElement | null>
  isLoading: boolean
  productListRef?: RefObject<HTMLDivElement | null>
  products: SearchResult[]
}

const ProductListContent = ({
  configStyles,
  enablePagination,
  error,
  firstItemRef,
  isLoading,
  productListRef,
  products,
  showBadges,
  showCta,
  showInStock,
  showPrice,
}: ProductListContentProps) => {
  const { t } = useTranslation()
  const noMatchingProducts = !isLoading && products.length === 0
  const requiresVisibleProduct = products.length > 0 && !error
  const isReady = useWidgetReadyAfterInitialLoad({
    hasSettledContent: products.length > 0 || Boolean(error) || noMatchingProducts,
    hasVisibleContent: !requiresVisibleProduct,
    isLoading,
  })

  const usePaginationFocusScroll = enablePagination && productListRef && firstItemRef

  return (
    <WidgetReadyBoundary isReady={isReady}>
      <div className={styles.productListHeader}></div>
      {noMatchingProducts && (
        <div className={styles.noProducts} role="status" aria-live="polite">
          <Typography variant="body">{t('productList.noProductsFound')}</Typography>
        </div>
      )}

      <div className={styles.productList} ref={productListRef}>
        {usePaginationFocusScroll && (
          <FirstItemFocusEffect products={products} firstItemRef={firstItemRef} />
        )}
        {error ? (
          <div className={styles.error} role="alert">
            <Typography variant="body">{t('general.genericErrorMessage')}</Typography>
          </div>
        ) : (
          <>
            {products.map((product: SearchResult, index: number) => {
              const item = usePaginationFocusScroll ? (
                <LazyLoad key={product.productId} offset={200}>
                  <ProductListItem
                    product={product}
                    showBadges={showBadges}
                    showCta={showCta}
                    showInStock={showInStock}
                    showPrice={showPrice}
                    configStyles={configStyles}
                  />
                </LazyLoad>
              ) : (
                <ProductListItem
                  key={product.productId}
                  product={product}
                  showBadges={showBadges}
                  showCta={showCta}
                  showInStock={showInStock}
                  showPrice={showPrice}
                  configStyles={configStyles}
                />
              )
              if (index === 0 && usePaginationFocusScroll) {
                return (
                  <div
                    key={product.productId}
                    ref={firstItemRef}
                    tabIndex={-1}
                    className={styles.firstItemFocusTarget}
                    aria-label={t('pagination.startOfProductList', 'Start of product list')}
                  >
                    {item}
                  </div>
                )
              }
              return item
            })}
          </>
        )}
      </div>

      {enablePagination && (
        <Pagination
          isLoading={isLoading}
          paginationButtonVariant={configStyles?.paginationButtonVariant?.variant ?? 'primary'}
          scrollTargetRef={productListRef}
        />
      )}
    </WidgetReadyBoundary>
  )
}

const ProductListComponent = ({
  productListIdentifier,
  facetValueIds,
  collectionId,
  enablePagination,
  take,
  showBadges,
  showCta,
  showInStock,
  showPrice,
  configStyles,
}: ProductListAttributes) => {
  const productListRef = useRef<HTMLDivElement>(null)
  const firstItemRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!enablePagination || typeof window === 'undefined') return
    const currentPage = getPageFromLocation(window.location)
    if (currentPage <= 1) return
    const id = window.setTimeout(() => {
      productListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, INITIAL_SCROLL_DELAY_MS)
    return () => window.clearTimeout(id)
  }, [enablePagination])

  const searchInputProps: SearchInputProps = {
    collectionId: collectionId,
    facetValueIds: facetValueIds,
    groupByProduct: true,
    take: take,
    sort: { price: SortOrder.Asc },
    term: getSearchTermFromUrl(),
  }

  return (
    <ProductList.Root
      productListIdentifier={productListIdentifier}
      searchInputProps={searchInputProps}
      infinitePagination={false}
      strategies={{
        paginationStoreStrategy: new WordPressPathPaginationStrategy(),
      }}
    >
      {({ products, error, isLoading }) => (
        <ProductListContent
          configStyles={configStyles}
          enablePagination={enablePagination}
          error={error}
          firstItemRef={firstItemRef}
          isLoading={isLoading}
          productListRef={productListRef}
          products={products}
          showBadges={showBadges}
          showCta={showCta}
          showInStock={showInStock}
          showPrice={showPrice}
        />
      )}
    </ProductList.Root>
  )
}

export default ProductListComponent
